import type { Rng } from '../rng';
import { luckOf, secondChanceFrame, secondChanceRate, type Outcome } from './luck';
import type { CasinoGame, TableOption } from './types';

/**
 * Simplified craps with three bets:
 * - Pass (~1.4% edge): come-out 7/11 wins, 2/3/12 loses, anything else sets
 *   a point; then roll until the point (win) or a 7 (lose).
 * - Don't Pass (~1.4%): the mirror image, with 12 on the come-out a push.
 * - Field (~5.6%): one roll; 3, 4, 9, 10, 11 pay 1:1, 2 and 12 pay 2:1.
 * While a point is on, you're locked in until it's decided.
 */
export type CrapsBet = 'pass' | 'dontpass' | 'field';
const BETS: CrapsBet[] = ['pass', 'dontpass', 'field'];
const BET_NAMES: Record<CrapsBet, string> = { pass: 'Pass', dontpass: "Don't Pass", field: 'Field' };

/** Points made in a row on Pass that win the jackpot. */
export const JACKPOT_POINTS = 5;

export interface CrapsState {
  bet: CrapsBet;
  /** The point, or null on the come-out roll. */
  point: number | null;
  /** Stake riding on the current Pass/Don't Pass decision. */
  stake: number;
  dice: [number, number] | null;
  message: string;
  /** Pass-line points made in a row (a Pass loss resets it). */
  pointStreak: number;
}

/** Frames of two dice tumbling before they land on `dice` (the faces are
 *  just for show, picked from the result so they're the same every time). */
export function tumbleFrames(dice: readonly [number, number]): string[] {
  return [3, 5, 2, 4].map((k, f) => {
    const a = ((dice[0] * 7 + k * (f + 3)) % 6) + 1;
    const b = ((dice[1] * 5 + k * (f + 2)) % 6) + 1;
    return `${f % 2 ? '  ' : ''}[${a}] ${f % 2 ? '' : '  '}[${b}]\n\nRolling...`;
  });
}

function roll(rng: Rng): [number, number] {
  return [rng.int(6) + 1, rng.int(6) + 1];
}

/** Field: total returned (stake included) for one roll. */
export function fieldReturn(sum: number, stake: number): number {
  if (sum === 12) return stake * 3;
  if (sum === 2) return stake * 3;
  if ([3, 4, 9, 10, 11].includes(sum)) return stake * 2;
  return 0;
}

/** The Field's results over the 36 rolls (for the ★'s second-chance rate). */
export function fieldOutcomes(): Outcome[] {
  const out: Outcome[] = [];
  for (let a = 1; a <= 6; a++) for (let b = 1; b <= 6; b++) out.push([1 / 36, fieldReturn(a + b, 1)]);
  return out;
}

/** The ★'s luck on Pass and Don't Pass: how often a roll that would lose
 *  the bet is rolled again, by luck level. Calibrated by sim to 103% / 110%;
 *  pinned in luck.test.ts. (The Field's rate is exact: fieldOutcomes.) */
// Don't Pass shares Pass's rates (its edge is almost the same, 1.36% vs 1.41%).
export const SECOND_CHANCE: Record<'pass' | 'dontpass', number[]> = { pass: [0, 0.1, 0.25], dontpass: [0, 0.1, 0.25] };

/** Would this roll lose the bet (come-out or point)? */
function losingRoll(state: CrapsState, sum: number): boolean {
  if (state.point === null) {
    if (state.bet === 'field') return fieldReturn(sum, 1) === 0;
    const natural = sum === 7 || sum === 11;
    const craps = sum === 2 || sum === 3 || sum === 12;
    return state.bet === 'pass' ? craps : natural; // Don't Pass loses on a natural, pushes a 12
  }
  return state.bet === 'pass' ? sum === 7 : sum === state.point;
}

export const craps: CasinoGame<CrapsState> = {
  id: 'craps',
  usesLuck: true,
  name: 'Craps',
  blurb: 'Pass, Don\'t Pass, or the one-roll Field.',
  baseMinBet: 5,
  baseMaxBet: 100,
  specialPerk: 'Luck: a losing roll is sometimes rolled again.',
  jackpotRule: `Make ${JACKPOT_POINTS} Pass points in a row.`,
  lockedHint: '• roll',
  create: () => ({ bet: 'pass', point: null, stake: 0, dice: null, message: '', pointStreak: 0 }),
  locked: (state) => state.point !== null,
  options(state): TableOption[] {
    if (state.point !== null) return [{ id: 'roll', label: `Roll (point ${state.point})` }];
    // 'betType', not 'bet': the casino layer already owns the 'bet' option.
    return [
      { id: 'roll', label: 'Roll' },
      { id: 'betType', label: `Bet on: ${BET_NAMES[state.bet]}` },
    ];
  },
  describe(state, env, highlighted) {
    if (highlighted === 'betType') {
      const field = '2 and 12 pay 2:1';
      return `Pass: 7/11 win, 2/3/12 lose, else hit the point before a 7.\nField: one roll; ${field}.\n\nTap to change.`;
    }
    const dice = state.dice ? `Dice: ${state.dice[0]} + ${state.dice[1]} = ${state.dice[0] + state.dice[1]}` : '';
    const streak = state.pointStreak > 0 ? `Pass points in a row: ${state.pointStreak}` : '';
    const lines = [dice, state.message, state.point !== null ? `Point is ${state.point}. Roll it before a 7.` : '', streak];
    return lines.filter(Boolean).join('\n') || `Betting ${BET_NAMES[state.bet]}. Tap Roll.`;
  },
  act(state, optionId, env) {
    if (optionId === 'betType' && state.point === null) {
      return { state: { ...state, bet: BETS[(BETS.indexOf(state.bet) + 1) % BETS.length]! }, bankroll: env.bankroll };
    }
    if (optionId !== 'roll') return { state, bankroll: env.bankroll };
    let dice = roll(env.rng);
    // The ★'s luck: a roll that would lose the bet is sometimes rolled again.
    const luck = luckOf(env);
    const q = state.bet === 'field' ? secondChanceRate(fieldOutcomes(), luck) : (SECOND_CHANCE[state.bet][luck] ?? 0);
    let lucky = false;
    let firstDice: [number, number] | null = null;
    if (losingRoll(state, dice[0] + dice[1]) && env.rng.next() < q) {
      const lost = dice;
      dice = roll(env.rng);
      lucky = !losingRoll(state, dice[0] + dice[1]);
      if (lucky) firstDice = lost;
    }
    // The dice tumble, then land; a ★ second chance shows the losing roll
    // first, then rolls again.
    const frames = firstDice
      ? [...tumbleFrames(firstDice), secondChanceFrame(`Dice: ${firstDice[0]} + ${firstDice[1]} = ${firstDice[0] + firstDice[1]}`), ...tumbleFrames(dice)]
      : tumbleFrames(dice);
    const sum = dice[0] + dice[1];
    const star = lucky ? '★ ' : '';

    // Come-out roll: the bet goes down now.
    if (state.point === null) {
      const stake = env.bet;
      const bankroll = env.bankroll - stake;
      if (state.bet === 'field') {
        const returned = fieldReturn(sum, stake);
        const message = returned ? `${star}Field wins +$${returned - stake}` : `Field loses -$${stake}`;
        return { state: { ...state, dice, message }, bankroll: bankroll + returned, settled: { wagered: stake, returned }, frames };
      }
      const pass = state.bet === 'pass';
      const natural = sum === 7 || sum === 11;
      const craps = sum === 2 || sum === 3 || sum === 12;
      if (natural || craps) {
        let returned = 0;
        if (pass) returned = natural ? stake * 2 : 0;
        else returned = sum === 12 ? stake : craps ? stake * 2 : 0;
        const net = returned - stake;
        const message = net > 0 ? `${star}Winner +$${net}` : net === 0 ? 'Push (12)' : `Loses -$${stake}`;
        const pointStreak = pass && !natural ? 0 : state.pointStreak;
        return { state: { ...state, dice, message, pointStreak }, bankroll: bankroll + returned, settled: { wagered: stake, returned }, frames };
      }
      return { state: { ...state, dice, point: sum, stake, message: '' }, bankroll, frames };
    }

    // Point is on: only the point or a 7 decides it.
    if (sum !== state.point && sum !== 7) return { state: { ...state, dice, message: 'No decision. Roll again.' }, bankroll: env.bankroll, frames };
    const pass = state.bet === 'pass';
    const passWins = sum === state.point;
    const won = pass ? passWins : !passWins;
    const returned = won ? state.stake * 2 : 0;
    const pointStreak = pass ? (passWins ? state.pointStreak + 1 : 0) : state.pointStreak;
    const message = won ? `${star}${passWins ? 'Point made' : 'Seven out'}! +$${state.stake}` : `${passWins ? 'Point made' : 'Seven out'}. -$${state.stake}`;
    return {
      state: { ...state, dice, point: null, stake: 0, message, pointStreak },
      bankroll: env.bankroll + returned,
      settled: { wagered: state.stake, returned },
      jackpot: pass && passWins && pointStreak === JACKPOT_POINTS,
      frames,
    };
  },
};
